<template>
  <Form @submit="handleRegister" :validation-schema="schema" class="card-primary max-w-md mx-2 md:mx-auto">

    <TiltedHeader>
      Inscription
    </TiltedHeader>

    <!-- Email -->
    <div class="flex items-center mt-4 border border-secondary rounded bg-background-light">
      <span class="px-3 bg-secondary text-text">
        <EnvelopeIcon class="w-6 h-10 text-white" />
      </span>
      <Field name="email" id="emailInputRegister" type="email"
        class="flex-1 px-3 py-2 bg-background text-text placeholder-secondary outline-none rounded-r"
        placeholder="Entrez votre adresse E-mail" :disabled="message" />
    </div>
    <ErrorMessage name="email" class="text-fun text-sm mt-1" />

    <!-- Password -->
    <div class="flex items-center mt-4 border border-secondary rounded bg-background-light">
      <span class="px-3 bg-secondary text-text">
        <KeyIcon class="w-6 h-10 text-white" />
      </span>
      <Field name="password" id="passwordInputRegister" type="password"
        class="flex-1 px-3 py-2 bg-background text-text placeholder-secondary outline-none rounded-r"
        placeholder="Entrez votre mot de passe" :disabled="message" />
    </div>
    <ErrorMessage name="password" class="text-fun text-sm mt-1" />

    <div class="mt-4 space-y-3 text-sm text-secondary">
      <label class="flex items-start gap-2">
        <Field name="acceptedCgu" type="checkbox" class="mt-1" />
        <span>
          J'accepte les <router-link to="/cgu" class="text-gold hover:underline">CGU</router-link>.
        </span>
      </label>
      <ErrorMessage name="acceptedCgu" class="text-fun text-sm" />

      <label class="flex items-start gap-2">
        <Field name="acceptedPrivacyPolicy" type="checkbox" class="mt-1" />
        <span>
          J'accepte la <router-link to="/politique-confidentialite" class="text-gold hover:underline">Politique de confidentialité</router-link>.
        </span>
      </label>
      <ErrorMessage name="acceptedPrivacyPolicy" class="text-fun text-sm" />
    </div>

    <!-- Submit button -->
    <button class="btn-primary w-full mt-6 gap-2 flex justify-center items-center" type="submit"
      :disabled="message || loading">

      <!-- Spinner -->
      <span v-if="loading" class="loader-sm mr-2"></span>

      <!-- Texte du bouton -->
      <span>{{ loading ? "Inscription en cours..." : "S'inscrire" }}</span>
    </button>


    <!-- Link -->
    <a href="#" @click.prevent="$emit('flipCard')"
      class="block text-xs text-gray-500 text-center mt-5 hover:text-gray-700">
      J'ai déjà un compte
    </a>
  </Form>

</template>
<script setup>
import { ref, inject } from "vue";
import { Form, Field, ErrorMessage, useForm } from "vee-validate";
import * as yup from "yup";
import { useAuthStore } from "@/stores/authStore";
import { useRouter } from "vue-router";
import TiltedHeader from "@/components/titles/TiltedHeader.vue";
import { EnvelopeIcon, KeyIcon } from '@heroicons/vue/24/outline'
import MessageComponent from "../ui/MessageComponent.vue";

const authStore = useAuthStore();
const router = useRouter();

const openOverlay = inject('openOverlay');
const closeOverlay = inject('closeOverlay');

if (authStore.isLoggedIn) {
  router.push("/home");
}

const schema = yup.object({
  email: yup
    .string()
    .required("E-mail requis !")
    .email("L'adresse e-mail renseignée est invalide")
    .max(50, "L'adresse e-mail ne peut pas dépasser 50 caractères !"),
  password: yup
    .string()
    .required("Vous devez entrer un mot de passe !")
    .min(6, "Votre mot de passe doit contenir au minimum 6 caractères !")
    .max(20, "Votre mot de passe ne doit pas excéder 20 caractères !"),
  acceptedCgu: yup
    .boolean()
    .oneOf([true], "Vous devez accepter les CGU."),
  acceptedPrivacyPolicy: yup
    .boolean()
    .oneOf([true], "Vous devez accepter la Politique de confidentialité."),
});

const LEGAL_VERSIONS = {
  cgu: '2026-07-17',
  privacyPolicy: '2026-07-17'
};

const loading = ref(false);
const message = ref(null);
const successful = ref(false);

const { resetForm } = useForm({});

async function handleRegister(user) {
  loading.value = true;

  const payload = {
    ...user,
    cguVersion: LEGAL_VERSIONS.cgu,
    privacyPolicyVersion: LEGAL_VERSIONS.privacyPolicy
  };

  try {
    const response = await authStore.register(payload);
    loading.value = false;
    message.value = response.message;
    successful.value = true;

    openOverlay(MessageComponent, { message: message.value, successful: successful.value, duration: 3000 });

    setTimeout(() => {
      closeOverlay();
      router.push(`/activation-info?email=${encodeURIComponent(payload.email)}`);
    }, 3050);

  } catch (error) {
    loading.value = false;
    message.value = error.response?.data?.message || "Une erreur est survenue lors de l'inscription.";
    successful.value = false;
    openOverlay(MessageComponent, { message: message.value, successful: successful.value, duration: 2000 });

    setTimeout(() => {
      resetForm();
      message.value = null;
    }, 2050);
  }
}
</script>

<style>
.tiny-text {
  font-size: 0.8em;
}
</style>